import React, { useCallback, useMemo } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon, Cog6ToothIcon } from '@heroicons/react/20/solid';
import { Link } from '@tanstack/react-router';
import { InfoIcon } from './Input';

/**
 * @typedef {Object} Preset
 * @property {string} id - Unique identifier for the preset
 * @property {string} name - Display name of the preset
 * @property {boolean} is_default - Whether this is the default preset
 */

/**
 * PresetSelector component for selecting compression presets
 * @param {Object} props
 * @param {Preset} [props.selectedPreset] - Currently selected preset
 * @param {Preset[]} [props.presets=[]] - Array of available presets
 * @param {(preset: Preset) => void} props.onSelect - Callback when a preset is selected
 * @param {string} [props.label] - Label text for the dropdown
 * @param {string} [props.tooltip] - Tooltip text explaining presets
 * @param {boolean} [props.isLoading] - Whether presets are currently loading
 * @param {string} [props.error] - Error message if preset loading/selection failed
 * @param {string} [props.className] - Additional CSS classes
 */
export function PresetSelector({ 
  selectedPreset,
  presets = [],
  onSelect,
  label = 'Compression Preset',
  tooltip = 'Select a preset to apply pre-configured compression settings',
  isLoading = false,
  error = '',
  className = ''
}) {
  // Memoize the filtered and sorted presets
  const sortedPresets = useMemo(() => {
    return [...presets].sort((a, b) => {
      // Default preset first, then alphabetically
      if (a.is_default !== b.is_default) {
        return a.is_default ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }, [presets]);

  // Memoize the selection handler
  const handleSelect = useCallback((preset) => {
    if (preset.id !== selectedPreset?.id) {
      onSelect(preset);
    }
  }, [selectedPreset, onSelect]);

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label} <InfoIcon tooltip={tooltip} />
      </label>

      <div className="relative">
        <Menu>
          {({ open }) => (
            <div>
              <Menu.Button
                className={`w-full flex items-center justify-between bg-background-soft border border-border/60 rounded-lg px-3.5 py-2.5 text-foreground hover:border-border focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all ${
                  error ? 'border-danger focus:ring-danger/20' : ''
                } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
                aria-label="Select compression preset"
                disabled={isLoading}
              >
                <span className="block truncate text-sm">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading presets...
                    </span>
                  ) : selectedPreset ? (
                    <span className="flex items-center gap-2">
                      {selectedPreset.name}
                      {selectedPreset.is_default && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-warning/10 text-warning rounded border border-warning/20">
                          Default
                        </span>
                      )}
                    </span>
                  ) : (
                    'Default options'
                  )}
                </span>
                <ChevronDownIcon 
                  className={`h-5 w-5 text-muted-foreground transition-transform duration-200 flex-shrink-0 ml-2 ${open ? 'transform rotate-180' : ''}`} 
                  aria-hidden="true" 
                />
              </Menu.Button>

              <Menu.Items 
                className="absolute z-10 mt-2 w-full bg-background-elevated border border-border/60 rounded-xl shadow-2xl focus:outline-none overflow-hidden"
                role="listbox"
              >
                <div className="py-1">
                  {sortedPresets.map((preset) => (
                    <Menu.Item key={preset.id}>
                      {({ active }) => (
                        <button
                          onClick={() => handleSelect(preset)}
                          className={`${
                            active ? 'bg-background-soft' : ''
                          } ${
                            selectedPreset?.id === preset.id ? 'bg-accent text-accent-foreground' : 'text-foreground'
                          } group flex w-full items-center justify-between px-4 py-2.5 text-sm transition-colors`}
                          role="option"
                          aria-selected={selectedPreset?.id === preset.id}
                        >
                          <span className="truncate">{preset.name}</span>
                          {preset.is_default && selectedPreset?.id !== preset.id && (
                            <span className="ml-2 flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-warning/10 text-warning rounded border border-warning/20">
                              Default
                            </span>
                          )}
                          {selectedPreset?.id === preset.id && (
                            <svg className="ml-2 w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {selectedPreset?.id === preset.id && (
                            <svg className="ml-2 w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </button>
                      )}
                    </Menu.Item>
                  ))}

                  <div className="border-t border-border/50 mt-1">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/app/presets"
                          className={`${
                            active ? 'bg-background-soft' : ''
                          } group flex items-center px-4 py-2.5 text-sm text-accent hover:text-accent-hover transition-colors`}
                          role="menuitem"
                        >
                          <Cog6ToothIcon className="mr-2 h-4 w-4" aria-hidden="true" />
                          Manage presets
                        </Link>
                      )}
                    </Menu.Item>
                  </div>
                </div>
              </Menu.Items>
            </div>
          )}
        </Menu>
      </div>

      {error && (
        <p className="mt-2 text-sm text-danger flex items-center gap-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
} 